import { useEffect, useState, useCallback } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import DateRangeSelector from '../../DateRangeSelector';
import FilterSelectorWithApp from '../../FilterSelectorWithApp';
import apiClient from '../../../utils/axiosClient';
import dayjs, { Dayjs } from 'dayjs';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';

type Props<T extends { dailyStats: unknown[] }> = {
  apiSlug: string;
  initialMetrics: T;
  renderDashboard: (data: T) => React.ReactNode;
  renderTable: (data: T) => React.ReactNode;
  tableWidth?: string;
};

export default function GuruPage<T extends { dailyStats: unknown[] },>({
  apiSlug,
  initialMetrics,
  renderDashboard,
  renderTable,
  tableWidth = '100%',
}: Props<T>) {
  const [metrics, setMetrics] = useState<T>(initialMetrics);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [initialized, setInitialized] = useState(false);
  const [selectedApp, setSelectedApp] = useState<string | null>('앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);

  const fetchFilterOptions = useCallback(() => {
    apiClient
      .get(`/api/v1/stat/adcash/guru/${apiSlug}/app/list`, {
        params: {
          start_date: startDate?.format('YYYY-MM-DD'),
          end_date: endDate?.format('YYYY-MM-DD'),
        },
      })
      .then((res) => setAppOptions(['앱 전체', ...res.data]));
  }, [apiSlug, startDate, endDate]);

  useEffect(() => { fetchFilterOptions(); }, [fetchFilterOptions]);
  useEffect(() => { setInitialized(true); }, []);

  const fetchMetrics = useCallback(() => {
    if (!startDate || !endDate || selectedApp === null) return;
    setLoading(true);
    const params: Record<string, string> = {
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      ...(selectedApp ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp } : {}),
    };
    apiClient
      .get(`/api/v1/stat/adcash/guru/${apiSlug}/detail/metrics`, { params })
      .then((res) => { setMetrics(res.data); setErrorMessage(''); })
      .catch(() => setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.'))
      .finally(() => setLoading(false));
  }, [apiSlug, startDate, endDate, selectedApp]);

  useEffect(() => {
    if (initialized && startDate && endDate && selectedApp != null) fetchMetrics();
  }, [fetchMetrics, initialized, startDate, endDate, selectedApp]);

  return (
    <Box sx={{ opacity: loading ? 0.3 : 1, transition: 'opacity 0.3s ease', minHeight: '100vh' }}>
      <Box alignItems="center" display="flex">
        <Box>
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onSearch={() => { if (startDate && endDate) fetchMetrics(); }}
          />
        </Box>
        <Box sx={{ ml: 3 }}>
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={setSelectedApp}
            onSearch={() => { if (startDate && endDate) fetchMetrics(); }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          ...PAGE_CARD_SX,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            mb: 1,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 700 : 400,
                backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                color: selectedApp === name ? '#fff' : '#374151',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': { backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6' },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider variant="fullWidth" sx={{ borderColor: '#e0e0e0', mb: 2 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>데이터 로딩 중...</Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : (
          <>
            {renderDashboard(metrics)}
            <Box
              sx={{
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                width: tableWidth,
                maxWidth: '100%',
                borderRadius: 3,
                p: 2,
                mt: 3,
              }}
            >
              {renderTable(metrics)}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
